import { useRef } from "react";
import { useLocalization } from "@fluent/react";
import { useNotificationContext } from "cerulean";
import { useHref } from "react-router-dom";
import { getDuplicateRoleUuid } from "./RoleFormUtils";

type Notification = Parameters<
  ReturnType<typeof useNotificationContext>["sendNotification"]
>[0];

/**
 * The error toast for a role save that matches another role's configuration. `showIfDuplicate`
 * shows it and returns `true` when a save error is a duplicate configuration. `clear` removes it,
 * since toasts outlive the page.
 */
export default function useDuplicateRoleNotification() {
  const { l10n } = useLocalization();
  const { sendNotification, deleteNotification } = useNotificationContext();
  // Toast links are plain links, not router links, so the href needs the router's base path.
  const rolesHref = useHref("/roles");
  const shownNotification = useRef<Notification | null>(null);

  const clear = () => {
    if (!shownNotification.current) return;
    deleteNotification(shownNotification.current);
    shownNotification.current = null;
  };

  const showIfDuplicate = (error: unknown) => {
    const uuid = getDuplicateRoleUuid(error);
    if (!uuid) return false;
    const message = l10n.getString("org-role-error-duplicate-configuration");
    const viewRole = l10n.getString("org-role-view-role");
    const notification: Notification = {
      type: "negative",
      text: `${message} [${viewRole}](${rolesHref}/${uuid})`,
      // Keeps the role form as it is.
      openLinksInNewTab: true,
    };
    sendNotification(notification);
    shownNotification.current = notification;
    return true;
  };

  return { clear, showIfDuplicate };
}
